import { MADEIRA_TZ } from './types'

/**
 * All grid maths runs on plain YYYY-MM-DD strings interpreted as Madeira
 * calendar days. Doing the arithmetic in UTC keeps it free of the WET/WEST
 * transition (Madeira is UTC+0 in winter, UTC+1 in summer) and of whatever
 * timezone the visitor's device happens to be in.
 */

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: MADEIRA_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: MADEIRA_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

/** Today's date in Madeira, as YYYY-MM-DD. */
export function madeiraToday(now: Date = new Date()): string {
  return dayFormatter.format(now)
}

/** An instant rendered as Madeira wall-clock HH:mm. */
export function madeiraTime(instant: Date): string {
  return timeFormatter.format(instant)
}

/** An instant split into the Madeira calendar day and wall-clock time it falls on. */
export function toMadeiraParts(instant: Date): { date: string; time: string } {
  return { date: dayFormatter.format(instant), time: timeFormatter.format(instant) }
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Monday of the week containing `date`. Madeira weeks start on Monday. */
export function startOfWeek(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  const dow = d.getUTCDay() // 0 = Sunday
  return addDays(date, dow === 0 ? -6 : 1 - dow)
}

export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
}

export function diffDays(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  return Math.round((b - a) / 86_400_000)
}

/** "HH:mm" -> minutes past midnight. */
export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

/** Minutes past midnight -> "HH:mm". */
export function fromMinutes(mins: number): string {
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, '0')
  const m = (mins % 60).toString().padStart(2, '0')
  return `${h}:${m}`
}

/**
 * Madeira's UTC offset in minutes on a given calendar day, derived from the
 * runtime's own tz database rather than a hardcoded DST rule.
 */
export function madeiraOffsetMinutes(date: string): number {
  // Midday avoids landing inside the 01:00 transition hour on switchover days.
  const probe = new Date(`${date}T12:00:00Z`)
  const local = new Date(probe.toLocaleString('en-US', { timeZone: MADEIRA_TZ }))
  const utc = new Date(probe.toLocaleString('en-US', { timeZone: 'UTC' }))
  return Math.round((local.getTime() - utc.getTime()) / 60_000)
}

/** Madeira wall-clock date + time -> the absolute instant it refers to. */
export function madeiraToUtc(date: string, hhmm: string): string {
  const offset = madeiraOffsetMinutes(date)
  const naive = Date.parse(`${date}T${hhmm}:00Z`)
  return new Date(naive - offset * 60_000).toISOString()
}
